// pages/ServicesPage.tsx - Updated without durations and stats
import React from 'react';
import { Link } from 'react-router-dom';
import {
  Wrench, Shield, Clock, Award, CheckCircle, ArrowRight,
  Anchor, Settings, Package, Zap, Users, Star, AlertCircle
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ServicesPage: React.FC = () => {
  const { t } = useLanguage();

  const services = [
    {
      id: 'repair',
      titleKey: 'services.repair.title',
      descKey: 'services.repair.desc',
      icon: <Wrench className="w-8 h-8" />,
      featureKeys: [
        'services.repair.feature1',
        'services.repair.feature2',
        'services.repair.feature3',
        'services.repair.feature4',
        'services.repair.feature5'
      ],
      priceKey: 'services.repair.price',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'maintenance',
      titleKey: 'services.maintenance.title',
      descKey: 'services.maintenance.desc',
      icon: <Settings className="w-8 h-8" />,
      featureKeys: [
        'services.maintenance.feature1',
        'services.maintenance.feature2',
        'services.maintenance.feature3',
        'services.maintenance.feature4',
        'services.maintenance.feature5'
      ],
      priceKey: 'services.maintenance.price',
      color: 'from-purple-500 to-pink-500'
    },
    {
      id: 'sparesparts',
      titleKey: 'services.parts.title',
      descKey: 'services.parts.desc',
      icon: <Package className="w-8 h-8" />,
      featureKeys: [
        'services.parts.feature1',
        'services.parts.feature2',
        'services.parts.feature3',
        'services.parts.feature4',
        'services.parts.feature5'
      ],
      priceKey: 'services.askQuote',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      id: 'inspection',
      titleKey: 'services.inspection.title',
      descKey: 'services.inspection.desc',
      icon: <Shield className="w-8 h-8" />,
      featureKeys: [
        'services.inspection.feature1',
        'services.inspection.feature2',
        'services.inspection.feature3',
        'services.inspection.feature4',
        'services.inspection.feature5'
      ],
      priceKey: 'services.inspection.price',
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'emergency',
      titleKey: 'services.emergency.title',
      descKey: 'services.emergency.desc',
      icon: <Zap className="w-8 h-8" />,
      featureKeys: [
        'services.emergency.feature1',
        'services.emergency.feature2',
        'services.emergency.feature3',
        'services.emergency.feature4',
        'services.emergency.feature5'
      ],
      priceKey: 'services.emergency.price',
      color: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-cyan-900 via-blue-900 to-teal-900 py-20 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('services.title')}
            </h1>
            <p className="text-xl text-cyan-100 max-w-3xl mx-auto mb-8">
              {t('services.subtitle')}
            </p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-gray-50 via-gray-50/50 to-transparent pointer-events-none" />
      </div>

      {/* Services Grid */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* First row: 3 items */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
            {services.slice(0, 3).map((service, index) => (
              <div key={index} className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden">
                {/* Full-box color wash */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`} />

                {/* Icon */}
                <div className="relative z-10 mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    {service.icon}
                  </div>
                </div>

                {/* Content */}
                <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 transition-all duration-300">
                  {t(service.titleKey)}
                </h3>

                <p className="relative z-10 text-gray-600 mb-6 leading-relaxed">
                  {t(service.descKey)}
                </p>

                {/* Features List */}
                <ul className="relative z-10 space-y-3 mb-6">
                  {service.featureKeys.map((featureKey, featureIdx) => (
                    <li key={featureIdx} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{t(featureKey)}</span>
                    </li>
                  ))}
                </ul>

                {/* Pricing */}
                <div className="relative z-10 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-2xl font-bold text-cyan-600">{t(service.priceKey)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Second row: 2 items centered */}
          <div className="flex justify-center gap-8">
            {services.slice(3, 5).map((service, index) => (
              <div key={index} className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden w-full md:w-[calc(33.333%-1rem)]">
                {/* Full-box color wash */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500 rounded-2xl`} />

                {/* Icon */}
                <div className="relative z-10 mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-2xl flex items-center justify-center text-white shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-300`}>
                    {service.icon}
                  </div>
                </div>

                {/* Content */}
                <h3 className="relative z-10 text-2xl font-bold text-gray-900 mb-4 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-pink-600 transition-all duration-300">
                  {t(service.titleKey)}
                </h3>

                <p className="relative z-10 text-gray-600 mb-6 leading-relaxed">
                  {t(service.descKey)}
                </p>

                {/* Features List */}
                <ul className="relative z-10 space-y-3 mb-6">
                  {service.featureKeys.map((featureKey, featureIdx) => (
                    <li key={featureIdx} className="flex items-start gap-2 text-gray-700">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm">{t(featureKey)}</span>
                    </li>
                  ))}
                </ul>

                {/* Pricing */}
                <div className="relative z-10 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-2xl font-bold text-cyan-600">{t(service.priceKey)}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Why Choose Us */}
      <div className="py-20 bg-gradient-to-br from-gray-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            {t('services.whyChoose')}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <Shield className="w-8 h-8" />,
                titleKey: 'home.why.warranty.title',
                descKey: 'home.why.warranty.desc'
              },
              {
                icon: <Award className="w-8 h-8" />,
                titleKey: 'home.why.expertise.title',
                descKey: 'home.why.expertise.desc'
              },
              {
                icon: <Star className="w-8 h-8" />,
                titleKey: 'home.why.quality.title',
                descKey: 'home.why.quality.desc'
              },
              {
                icon: <Clock className="w-8 h-8" />,
                titleKey: 'home.why.flexibility.title',
                descKey: 'home.why.flexibility.desc'
              },
              {
                icon: <Users className="w-8 h-8" />,
                titleKey: 'home.why.service.title',
                descKey: 'home.why.service.desc'
              },
              {
                icon: <CheckCircle className="w-8 h-8" />,
                titleKey: 'home.why.pricing.title',
                descKey: 'home.why.pricing.desc'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center text-white mb-4">
                  {item.icon}
                </div>
                <h3 className="font-bold text-lg mb-2">{t(item.titleKey)}</h3>
                <p className="text-gray-600">{t(item.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

    </div>
  );
};

export default ServicesPage;
